import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import { Delete, Gavel } from '@mui/icons-material';
import {
  useCreateOfficial,
  useDeleteOfficial,
  useOfficial,
  useOfficials,
  useSports,
  useUpdateOfficial,
} from '@/hooks/useApi';
import { useAdminMode } from '@/hooks/useManage';
import { CatalogList, ErrorState, LoadingState, useCatalogParams } from '@/components';
import { FormPage, KebabMenu } from '@/components/manage';
import { useNotification } from '@/contexts/NotificationContext';
import type { Official, SportType } from '@/types';
import { sortSports, sortSportTypes } from '@/utils/sports';
import type { CitySuggestionOption } from '@/utils/dadata';
import { fetchCitySuggestions } from '@/utils/dadata';
import { ensureCity, getCityById } from '@/utils/cityRegistry';
import { getApiErrorMessage } from '@/utils/apiError';
import { isUUID } from '@/utils/uuid';

type OfficialRole = 'referee' | 'umpire' | 'arbiter';

const roleLabels: Record<OfficialRole, string> = {
  referee: 'Судья',
  umpire: 'Ампайр',
  arbiter: 'Арбитр',
};

const dadataToken = import.meta.env.VITE_DADATA_TOKEN?.trim() ?? '';

export const OfficialsPage: React.FC = () => {
  const { q, page } = useCatalogParams();
  const { adminMode, create } = useAdminMode();
  const { data, isLoading, error, refetch } = useOfficials({ page, limit: 20, q });
  const { data: sports } = useSports();

  return (
    <CatalogList
      title="Судьи"
      icon={<Gavel color="primary" />}
      isLoading={isLoading}
      error={error}
      onRetry={refetch}
      totalPages={data?.totalPages}
      createHref={create.official ? '/officials/new' : undefined}
      emptyText="Судей пока нет"
      rows={(data?.data ?? []).map((official) => ({
        id: official.id,
        title: official.name,
        muted: !official.active,
        href: adminMode ? `/officials/${official.id}/edit` : undefined,
        subtitle: (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {official.cityName && <Chip label={official.cityName} size="small" />}
            {official.roles?.map((role) => <Chip key={role} label={roleLabels[role]} size="small" />)}
            {sortSportTypes(official.sportTypes || []).map((st) => (
              <Chip key={st} label={sports?.find((s) => s.code === st)?.name || st} size="small" variant="outlined" />
            ))}
          </Box>
        ),
      }))}
    />
  );
};

type OfficialForm = {
  name: string;
  cityId: string;
  cityLabel: string;
  active: boolean;
  sportTypes: SportType[];
  roles: OfficialRole[];
};

const OfficialFormInner: React.FC<{ official?: Official }> = ({ official }) => {
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { can } = useAdminMode();
  const { data: sports } = useSports();
  const sortedSports = sortSports(sports || []);
  const [form, setForm] = useState<OfficialForm>({
    name: official?.name ?? '',
    cityId: official?.cityId ?? '',
    cityLabel: official?.cityName || getCityById(official?.cityId || undefined)?.name || '',
    active: official?.active ?? true,
    sportTypes: official?.sportTypes ?? [],
    roles: official?.roles ?? [],
  });
  const [dirty, setDirty] = useState(false);
  const [cityInput, setCityInput] = useState(form.cityLabel);
  const [cityOptions, setCityOptions] = useState<CitySuggestionOption[]>([]);
  const [cityLoading, setCityLoading] = useState(false);
  const createOfficial = useCreateOfficial();
  const updateOfficial = useUpdateOfficial();
  const deleteOfficial = useDeleteOfficial();

  const patch = (next: Partial<OfficialForm>) => {
    setForm((prev) => ({ ...prev, ...next }));
    setDirty(true);
  };

  useEffect(() => {
    const query = cityInput.trim();
    if (!dadataToken || query.length < 3) {
      setCityOptions([]);
      return;
    }
    let active = true;
    const handle = window.setTimeout(async () => {
      setCityLoading(true);
      try {
        const options = await fetchCitySuggestions(query, dadataToken);
        if (active) setCityOptions(options);
      } catch {
        if (active) setCityOptions([]);
      } finally {
        if (active) setCityLoading(false);
      }
    }, 400);
    return () => {
      active = false;
      window.clearTimeout(handle);
    };
  }, [cityInput]);

  const submit = async () => {
    const payload = { ...form, cityId: isUUID(form.cityId) ? form.cityId : null };
    try {
      if (official) await updateOfficial.mutateAsync({ id: official.id, data: payload });
      else await createOfficial.mutateAsync(payload);
      setDirty(false);
      showSuccess('Судья сохранён');
      navigate('/officials', { replace: true });
    } catch (err) {
      showError(getApiErrorMessage(err, 'Не удалось сохранить судью.'));
    }
  };

  return (
    <FormPage
      title={official ? 'Судья' : 'Новый судья'}
      onSubmit={() => void submit()}
      saving={createOfficial.isPending || updateOfficial.isPending}
      submitDisabled={!form.name || (official ? !can(official.permissions?.update) : false)}
      dirty={dirty}
      closeTo="/officials"
      headerActions={
        official && (
          <KebabMenu
            items={[
              {
                id: 'delete',
                label: 'Удалить',
                icon: <Delete fontSize="small" />,
                danger: true,
                hidden: !can(official.permissions?.delete),
                confirm: { title: 'Удалить судью?' },
                onClick: async () => {
                  await deleteOfficial.mutateAsync(official.id);
                  navigate('/officials', { replace: true });
                },
              },
            ]}
          />
        )
      }
    >
      <Stack spacing={2} sx={{ pt: 1 }}>
        <TextField label="ФИО" required fullWidth autoComplete="name" value={form.name} onChange={(e) => patch({ name: e.target.value })} />
        <Autocomplete
          freeSolo
          options={cityOptions.map((o) => o.label)}
          value={form.cityLabel}
          inputValue={cityInput}
          onInputChange={(_, value) => {
            setCityInput(value);
            patch({ cityLabel: value });
          }}
          onChange={(_, value) => {
            const label = typeof value === 'string' ? value : '';
            setCityInput(label);
            const selected = cityOptions.find((o) => o.label === label);
            const city = selected ? ensureCity(selected) : undefined;
            patch({ cityId: city?.id || '', cityLabel: label });
          }}
          noOptionsText={cityInput.trim().length < 3 ? 'Введите минимум 3 символа' : 'Города не найдены'}
          loading={cityLoading}
          disabled={!dadataToken}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Город"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {cityLoading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
        <FormControl fullWidth>
          <InputLabel>Виды спорта</InputLabel>
          <Select
            multiple
            value={form.sportTypes}
            onChange={(e) => patch({ sportTypes: e.target.value as SportType[] })}
            input={<OutlinedInput label="Виды спорта" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {sortSportTypes(selected as SportType[]).map((code) => (
                  <Chip key={code} label={sports?.find((s) => s.code === code)?.name || code} size="small" />
                ))}
              </Box>
            )}
          >
            {sortedSports.map((sport) => (
              <MenuItem key={sport.code} value={sport.code}>
                <Checkbox checked={form.sportTypes.includes(sport.code)} size="small" />
                <ListItemText primary={sport.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth>
          <InputLabel>Роли</InputLabel>
          <Select
            multiple
            value={form.roles}
            onChange={(e) => patch({ roles: e.target.value as OfficialRole[] })}
            input={<OutlinedInput label="Роли" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as OfficialRole[]).map((value) => (
                  <Chip key={value} label={roleLabels[value]} size="small" />
                ))}
              </Box>
            )}
          >
            {(Object.keys(roleLabels) as OfficialRole[]).map((role) => (
              <MenuItem key={role} value={role}>
                <Checkbox checked={form.roles.includes(role)} size="small" />
                <ListItemText primary={roleLabels[role]} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          sx={{ minHeight: 48 }}
          control={<Switch checked={form.active} onChange={(e) => patch({ active: e.target.checked })} />}
          label="Активен"
        />
      </Stack>
    </FormPage>
  );
};

export const OfficialFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { data: official, isLoading, error, refetch } = useOfficial(id ?? '', { queryKey: ['officials', id ?? ''], enabled: Boolean(id) });
  if (!id) return <OfficialFormInner />;
  if (isLoading) return <LoadingState fullscreen />;
  if (error || !official) return <ErrorState message="Судья не найден" onRetry={refetch} />;
  return <OfficialFormInner key={official.id} official={official} />;
};
